import { useMemo } from 'react';
import { useTenantAuthStore } from '@/store/tenantAuthStore';
import { hasPageAccess, DEFAULT_LANDING_PAGE, PAGE_PERMISSIONS } from '@/config/permissions';
import { ROUTES } from '@/config/routes';

/**
 * Hook to determine the appropriate landing page for a user
 * Priority:
 * 1. Tenant-configured landing page (from tenant.settings.landing_page)
 * 2. First accessible page based on user's roles
 * 3. Default landing page (/dashboard)
 */
export function useLandingPage(): string {
  const { user } = useTenantAuthStore();

  return useMemo(() => {
    if (!user) {
      return ROUTES.HOME;
    }

    const userRoles = user.roles || [];

    // Priority 1: Tenant-configured landing page
    const tenantLandingPage = user.tenant?.settings?.landing_page;
    if (tenantLandingPage) {
      // Validate user has access to the configured landing page
      if (hasPageAccess(userRoles, tenantLandingPage)) {
        return tenantLandingPage;
      }
      // If user doesn't have access, fall through to next priority
      console.warn(
        `[useLandingPage] Tenant landing page "${tenantLandingPage}" not accessible, finding alternative`,
        { userRoles }
      );
    }

    // Priority 2: First accessible page based on user's roles
    const accessiblePages = Object.keys(PAGE_PERMISSIONS).filter(page =>
      hasPageAccess(userRoles, page)
    );

    if (accessiblePages.length > 0) {
      // Prefer /dashboard if accessible
      if (accessiblePages.includes(DEFAULT_LANDING_PAGE)) {
        return DEFAULT_LANDING_PAGE;
      }
      // Otherwise return first accessible page
      return accessiblePages[0];
    }

    // Priority 3: Default landing page (even if not accessible)
    // This will trigger role protection and show forbidden page
    return DEFAULT_LANDING_PAGE;
  }, [user]);
}
